import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { faCalendarAlt, faLink, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { VssEvent } from 'src/app/shared/models/vss-event.module';
import { EventType, EventsService } from '../events.service';
import { Schedule } from 'src/app/shared/models/schedule.model';
import { environment } from 'src/environments/environment';
import moment from 'moment';
import { ScheduleTime } from 'src/app/shared/models/schedule-time.model';
import { ToastService } from 'src/app/shared/toast/toast.service';

@Component({
    selector: 'app-update-event',
    templateUrl: './update-event.component.html',
    styleUrls: ['./update-event.component.css'],
    standalone: false
})
export class UpdateEventComponent implements OnInit {

  @ViewChild('eventForm') eventForm: NgForm;

  public currentOrganizationId: number;
  public eventFormGroup: FormGroup;
  public currentLang;
  public currentOrgLanguage = 'de';
  public languageToString = ({
    'de': 'German - DE-de',
    'it': 'Italian - IT-it',
    'en': 'Ladin - lld',
  });
  public icons: { [name: string]: any } = {
    'faLink': faLink,
    'faCalendarAlt': faCalendarAlt,
    'faTimes': faTimes
  };
  public eventTypes: EventType[] = [];
  public eventHourFormArray = new FormArray([]);
  public endDateBeforeStartDateError = false;

  public imagePath: any;
  public fileSizeError = false;
  public eventImages: { id: number, fileName: string, filePath: string, isMain: boolean }[] = [];
  public imageUrl = environment.imageUrl;

  public eventTypeDisabled = false;
  public currentEventTypeId: string;

  private _eventId: number;
  private _imageToUpload: File = null;

  constructor(private _activatedRoute: ActivatedRoute, private _router: Router, private _formBuilder: FormBuilder,
    private _translate: TranslateService, private _eventsService: EventsService, private _toastService: ToastService) {
    this.eventFormGroup = this._formBuilder.group({
      id: [''],
      name_it: [''],
      name_de: [''],
      name_en: [''],
      location_en: [''],
      location_it: [''],
      location_de: [''],
      startDate: ['', [Validators.required]],
      endDate: ['', [Validators.required]],
      eventTypeId: ['1', [Validators.required]],
      organizationId: [this.currentOrganizationId],
      description_en: [''],
      description_it: [''],
      description_de: [''],
      eventHour: new FormArray([])
    });
  }

  ngOnInit(): void {
    this.currentLang = this._translate.currentLang;
    const urlPramas = this._activatedRoute.snapshot.params;
    if (!urlPramas.orgId) {
      this._router.navigate(['']);
    }
    this.currentOrganizationId = urlPramas.orgId;
    this._eventId = urlPramas.eventId;
    const obsArray: any[] = [
      this._eventsService.getEventTypes()
    ];
    if (this._eventId) {
      obsArray.push(this._eventsService.getOrganizationEvents(this.currentOrganizationId.toString(), this._eventId));
    }
    combineLatest(obsArray).subscribe((result: any[]) => {
      const eventTypes: EventType[] = result[0];
      const event: VssEvent = result[1];
      this.eventTypes = eventTypes;
      console.log(event);
      if (event) {
        event.startDate = Schedule.convertDateToNgbDataStructure(event.startDate);
        event.endDate = Schedule.convertDateToNgbDataStructure(event.endDate);
        event.eventTypeId = event.eventTypeId.toString();
        this.eventFormGroup.patchValue(event, { emitEvent: false });
        this.eventImages = event.images;

        this.eventFormGroup.get('eventTypeId').disable();
        this.currentEventTypeId = event.eventTypeId;

        const eventHourFormArray = this.eventFormGroup.get('eventHour') as FormArray;
        event.eventHour.map((h, i) => {
          if (this.eventFormGroup.get('eventTypeId').value === '7') {
            eventHourFormArray.push(this._formBuilder.group({
              id: h.id,
              eventDate: h.eventDate,
              startHour: h.startHour,
              endHour: h.endHour,
              openingDays: null,
              status: 1
            }));
          } else {
            h.openingDays = ScheduleTime.convertDaysStringToObject(h.openingDays);
            eventHourFormArray.push(this._formBuilder.group({
              id: null,
              eventDate: null,
              startHour: h.startHour,
              endHour: h.endHour,
              openingDays: new FormGroup({
                '1': new FormControl(h.openingDays['1']),
                '2': new FormControl(h.openingDays['2']),
                '3': new FormControl(h.openingDays['3']),
                '4': new FormControl(h.openingDays['4']),
                '5': new FormControl(h.openingDays['5']),
                '6': new FormControl(h.openingDays['6']),
                '7': new FormControl(h.openingDays['7']),
              }),
              status: 1
            }));
          }
        });
      } else {
        if (this._activatedRoute.snapshot.url[this._activatedRoute.snapshot.url.length - 1].path === 'update' && !event) {
          return this._router.navigate(['', 'organization', this.currentOrganizationId, 'events']);
        }
      }
      this.eventFormGroup.get('startDate').valueChanges.subscribe(_ => this._getDateDiff());
      this.eventFormGroup.get('endDate').valueChanges.subscribe(_ => this._getDateDiff());
    });
  }

  get eventFormGroupFormArray() {
    return this.eventFormGroup.get('eventHour')['controls'] as FormGroup[];
  }

  onLanguageChange(language: string) {
    this.currentOrgLanguage = language;
  }

  submitForm() {
    this._toastService.remove();
    if (this.eventFormGroup.valid) {
      const object = this.eventFormGroup.value as VssEvent;
      object.startDate = Schedule.convertNgbDataStructureToDateString(object.startDate);
      object.endDate = Schedule.convertNgbDataStructureToDateString(object.endDate);
      object.organizationId = this.currentOrganizationId;
      object.eventHour.map(h => {
        if (h.openingDays !== null) {
          h.openingDays = ScheduleTime.convertDaysObjectToArray(h.openingDays);
        }
      });
      console.log(object);
      let obs = this._eventsService.createOrganizationEvent(object);
      if (this._eventId) {
        object.eventTypeId = this.currentEventTypeId;
        obs = this._eventsService.updateOrganizationEvent(object);
      }
      obs.subscribe(result => {
        if (this._eventId) {
          if (this._imageToUpload) {
            this._eventsService.submitEventImage(this._imageToUpload, this._eventId).subscribe(imageResult => {
              this.eventImages.push(imageResult);
              this._imageToUpload = null;
            }, error => {
              console.error(error);
            });
          }
          this._toastService.remove();
          this._toastService.show('', {
            classname: 'bg-success text-light',
            delay: 2000,
            autohide: true,
          });
          this.reloadCurrentRoute();
        } else {
          if (this._imageToUpload) {
            this._eventsService.submitEventImage(this._imageToUpload, result.id).subscribe(imageResult => {
              this.eventImages.push(imageResult);
              this._imageToUpload = null;
              this._toastService.remove();
              this._toastService.show('', {
                classname: 'bg-success text-light',
                delay: 2000,
                autohide: true,
              });
              this._router.navigate(['organization', this.currentOrganizationId, 'events', result.id, 'update']);
            }, error => {
              console.error(error);
            });
          } else {
            this._toastService.remove();
            this._toastService.show('', {
              classname: 'bg-success text-light',
              delay: 2000,
              autohide: true,
            });
            this._router.navigate(['organization', this.currentOrganizationId, 'events', result.id, 'update']);
          }
        }
      });
    }
  }

  handleImageFileInput(event: any) {
    const files = event.target.files;
    this.fileSizeError = false;
    if (files.length === 0) { return; }
    if (files.length > 1) { return; }
    const mimeType = files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      // return this._toastr.warning(this._translate.instant('event.advertiseEvent.imageError'));
      return;
    }
    if (files[0].size > 9500000) {
      this.fileSizeError = true;
      return;
    }
    const reader = new FileReader();
    this._imageToUpload = files.item(0);
    reader.readAsDataURL(files[0]);
    reader.onload = (_event) => {
      this.imagePath = reader.result;
    };
  }

  private _getDateDiff(): void {
    this.endDateBeforeStartDateError = false;
    const startDate = Schedule.convertNgbDataStructureToDateString(this.eventFormGroup.get('startDate').value);
    const endDate = Schedule.convertNgbDataStructureToDateString(this.eventFormGroup.get('endDate').value);
    if (!startDate || !endDate) {
      return;
    }
    console.log(moment(endDate).isBefore(startDate));
    if (moment(endDate).isBefore(startDate)) {
      this.endDateBeforeStartDateError = true;
      return;
    }
    const datesArray = this._enumerateDaysBetweenDates(startDate, endDate);
    const eventHourFormArray = this.eventFormGroup.get('eventHour') as FormArray;
    eventHourFormArray.clear();
    datesArray.forEach(date => {
      if (this.eventFormGroup.get('eventTypeId').value === '7') {
        eventHourFormArray.push(this._formBuilder.group({
          id: null,
          eventDate: moment(date).format('YYYY-MM-DD'),
          startHour: '00:00',
          endHour: '00:00',
          openingDays: null,
          status: 1
        }));
      }
    });
  }

  public onAddTimeScheduleElement() {
    (<FormArray>this.eventFormGroup.get('eventHour')).push(this._formBuilder.group({
      id: null,
      eventDate: null,
      startHour: '00:00',
      endHour: '00:00',
      openingDays: new FormGroup({
        '1': new FormControl(false),
        '2': new FormControl(false),
        '3': new FormControl(false),
        '4': new FormControl(false),
        '5': new FormControl(false),
        '6': new FormControl(false),
        '7': new FormControl(false),
      }),
      status: 1
    }));
  }

  deleteImage(imageId: number, index: number) {
    this._eventsService.deleteEventImage(imageId).subscribe(result => {
      this.eventImages.splice(index, 1);
    });
  }

  deleteScheduleTimeControl(index: number) {
    (<FormArray>this.eventFormGroup.get('eventHour')).removeAt(index);
  }

  setImageAsMain(imageId: number, index: number) {
    this._eventsService.setMainEventImage(imageId).subscribe(result => {
      console.log(result);
      this.eventImages.forEach(image => {
        if (image.id === imageId) {
          image.isMain = true;
        } else {
          image.isMain = false;
        }
      });
    });
  }

  private _enumerateDaysBetweenDates(startDate: string, endDate: string) {
    const dates = [];
    const currDate = moment(startDate).subtract(1, 'day').startOf('day');
    const lastDate = moment(endDate).add(1, 'day').startOf('day');
    while (currDate.add(1, 'days').diff(lastDate) < 0) {
      dates.push(currDate.clone().toDate());
    }
    return dates;
  }

  public reloadCurrentRoute() {
    const currentUrl = this._router.url;
    this._router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
        this._router.navigate([currentUrl]);
    });
}

}
